import React, { useEffect, useState } from 'react';
import { fetchBuyerActionItems, sortByUrgency, waitingLabel } from '../../lib/buyerActionItems';
import { MANAGED_STAGES, stageIndexOf, isCancelled } from '../../lib/managedStages';
import TranslatedText from '../TranslatedText';

// Trader Home «لوحتي» — the design canvas (owner, 2026-10-21, Apple HIG): one job
// per block, the managed order first. Order: managed order (dark tracker with the
// 10 stages, or the invite) → «يحتاج تصرّفك» → «ابدأ طلباً جديداً» (managed ·
// quote · idea) → «نشاطك». The store and messages live on the tab bar / navbar,
// so they are not repeated here.

const BRONZE = '#8B7355', INK = '#1A1814', RED = '#C0503F', GOLD = '#D9C39B';

const T = {
  ar: {
    eyebrow: 'مَعبر · لوحة التاجر', hi: 'أهلاً', sub: 'كل ما يحتاج تصرّفك، ومنتجاتك ومصانعك.',
    messages: 'الرسائل',
    managedK: 'الطلب المُدار', managedInviteTitle: 'دع مَعبر تتكفّل بطلبك',
    managedInvitePitch: 'من البحث والتفاوض حتى باب بيتك — أنت توافق، وإحنا نتولّى الباقي.',
    managedStart: 'ابدأ طلباً مُداراً ←', managedTrack: 'متابعة الطلب المُدار ←',
    stageOf: (i, n) => `المرحلة ${i} من ${n}`, offerReady: 'عرضك المنسّق جاهز',
    browseProducts: 'المنتجات ←', browseFactories: 'المصانع ←',
    needs: 'يحتاج تصرّفك', activity: 'نشاطك',
    active: 'طلبات نشطة', offers: 'عروض جديدة', msgs: 'رسائل', done: 'مكتملة',
    kinds: { managed: 'طلب مُدار', rfq: 'طلب عرض سعر', direct: 'شراء مباشر', sample: 'عيّنة', inquiry: 'استفسار', message: 'رسالة' },
    acts: { review_offers: 'قارن العروض ←', pay_order: 'ادفع ←', confirm_receipt: 'أكّد الاستلام ←', view_inquiry: 'عرض الاستفسار ←', reply_message: 'ردّ ←', view_request: 'عرض الطلب ←', track_order: 'تتبّع ←', view_sample: 'عرض العيّنة ←' },
    party: 'مورد',
    managedMine: 'طلبك المُدار', next: (s) => `التالي: ${s}`, track: 'متابعة الطلب',
    startNew: 'ابدأ طلباً جديداً',
    newTiles: { managed: ['مُدار', 'نتولّى كل شي'], rfq: ['عرض سعر', 'المصانع تتنافس'], idea: ['فكرتك', 'بتصميمك'] },
  },
  en: {
    eyebrow: 'Maabar · Trader dashboard', hi: 'Welcome', sub: 'Everything that needs you, your products and your factories.',
    messages: 'Messages',
    managedK: 'Managed order', managedInviteTitle: 'Let Maabar handle it',
    managedInvitePitch: 'From sourcing and negotiation to your door — you approve, we handle the rest.',
    managedStart: 'Start a managed order →', managedTrack: 'Track managed order →',
    stageOf: (i, n) => `Stage ${i} of ${n}`, offerReady: 'Your curated offer is ready',
    browseProducts: 'Products →', browseFactories: 'Factories →',
    needs: 'Needs your action', activity: 'Your activity',
    active: 'Active', offers: 'New offers', msgs: 'Messages', done: 'Completed',
    kinds: { managed: 'Managed order', rfq: 'Quote request', direct: 'Direct purchase', sample: 'Sample', inquiry: 'Inquiry', message: 'Message' },
    acts: { review_offers: 'Compare offers →', pay_order: 'Pay →', confirm_receipt: 'Confirm receipt →', view_inquiry: 'View inquiry →', reply_message: 'Reply →', view_request: 'View request →', track_order: 'Track →', view_sample: 'View sample →' },
    party: 'Supplier',
    managedMine: 'Your managed order', next: (s) => `Next: ${s}`, track: 'Track order',
    startNew: 'Start a new request',
    newTiles: { managed: ['Managed', 'We handle it all'], rfq: ['Quote', 'Factories compete'], idea: ['Your idea', 'Your design'] },
  },
  zh: {
    eyebrow: 'Maabar · 采购商面板', hi: '您好', sub: '所有需要您处理的事项、您的产品与工厂。',
    messages: '消息',
    managedK: '托管订单', managedInviteTitle: '让 Maabar 为您代劳',
    managedInvitePitch: '从寻源谈判到送货上门——您批准，其余由我们负责。',
    managedStart: '开始托管订单 →', managedTrack: '跟踪托管订单 →',
    stageOf: (i, n) => `第 ${i}/${n} 阶段`, offerReady: '您的精选报价已就绪',
    browseProducts: '产品 →', browseFactories: '工厂 →',
    needs: '待您处理', activity: '您的活动',
    active: '进行中', offers: '新报价', msgs: '消息', done: '已完成',
    kinds: { managed: '托管订单', rfq: '询价', direct: '直接采购', sample: '样品', inquiry: '咨询', message: '消息' },
    acts: { review_offers: '比较报价 →', pay_order: '支付 →', confirm_receipt: '确认收货 →', view_inquiry: '查看咨询 →', reply_message: '回复 →', view_request: '查看需求 →', track_order: '跟踪 →', view_sample: '查看样品 →' },
    party: '供应商',
    managedMine: '您的托管订单', next: (s) => `下一步：${s}`, track: '跟踪订单',
    startNew: '发起新需求',
    newTiles: { managed: ['托管', '全程代办'], rfq: ['询价', '工厂竞价'], idea: ['创意', '您的设计'] },
  },
};

const RED_ACTIONS = new Set(['pay_order', 'confirm_receipt']);

// showHeader: the dashboard page already greets the trader and its header bar
// carries Messages, so it passes false — two greetings and a third Messages
// button on one screen was the «حوسة» (owner, 2026-10-20).
export default function BuyerHomePanel({ sb, buyerId, lang = 'ar', name = '', messagesCount = 0, onOpenRequests, onOpenProducts, onOpenSuppliers, onNewManaged, onNewRfq, onNewIdea, onOpenManaged, onOpenMessages, showHeader = true }) {
  const c = T[lang] || T.ar;
  const isAr = lang === 'ar';
  const font = isAr ? { fontFamily: 'var(--font-ar)' } : {};
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [names, setNames] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      const { items: it, counts: ct } = await fetchBuyerActionItems(sb, buyerId, lang);
      if (!alive) return;
      setItems(it); setCounts(ct);
      const ids = [...new Set(it.map((x) => x.partyId).filter(Boolean))];
      if (ids.length) {
        const { data } = await sb.from('profile_directory').select('id, full_name, company_name').in('id', ids);
        if (alive && data) setNames(data.reduce((a, p) => ({ ...a, [p.id]: p.company_name || p.full_name }), {}));
      }
    })();
    return () => { alive = false; };
  }, [sb, buyerId, lang]);

  // Latest non-cancelled managed order → tracker; otherwise invite.
  const managed = sortByUrgency(items.filter((x) => x.kind === 'managed' && !isCancelled(x.status)))
    .filter((x) => x.bucket !== 'completed');
  const activeManaged = managed[0] || null;
  // Needs-action list excludes the managed order (it has its own box above).
  const needs = sortByUrgency(items.filter((x) => x.bucket === 'needs_action' && x.kind !== 'managed')).slice(0, 3);
  const offersNew = items.filter((x) => x.action === 'review_offers').length;

  const Stat = ({ v, k, red, go }) => (
    <button type="button" onClick={go} style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '14px 12px', textAlign: isAr ? 'right' : 'left', cursor: go ? 'pointer' : 'default' }}>
      <div style={{ fontFamily: 'Inter, var(--font-sans), sans-serif', fontSize: 24, fontWeight: 600, lineHeight: 1, color: red && v > 0 ? RED : 'var(--text-primary)' }}>{v}</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, ...font }}>{k}</div>
    </button>
  );

  return (
    <div style={{ maxWidth: 640 }}>
      {/* header */}
      {showHeader && <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <div style={{ fontSize: 11, color: BRONZE, fontWeight: 600, ...font }}>{c.eyebrow}</div>
          <h1 style={{ fontSize: isAr ? 24 : 28, fontWeight: 700, lineHeight: 1.3, color: 'var(--text-primary)', margin: '4px 0 0', overflowWrap: 'anywhere', ...font }}>{c.hi}{name ? `، ${name}` : ''}</h1>
          <p style={{ fontSize: 13, color: 'var(--text-disabled)', margin: '5px 0 0', maxWidth: 420, ...font }}>{c.sub}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <button onClick={onOpenMessages} style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 999, padding: '7px 13px', fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer', ...font }}>
            {c.messages}
            {messagesCount > 0 && <span style={{ background: RED, color: '#fff', fontSize: 10, fontWeight: 700, minWidth: 17, height: 17, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{messagesCount}</span>}
          </button>
          <div style={{ width: 34, height: 34, borderRadius: '50%', background: BRONZE, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>{(name || 'ت').trim().charAt(0)}</div>
        </div>
      </div>}

      {/* managed order — the tracker (10 stages) or, with none running, the invite */}
      {activeManaged ? (() => {
        const idx = stageIndexOf(activeManaged.status);
        const stage = MANAGED_STAGES[idx];
        const nextStage = MANAGED_STAGES[idx + 1];
        const label = (st) => st[lang] || st.ar;
        return (
          <div data-managed-tracker={activeManaged.requestId} style={{ background: INK, color: '#FAF8F5', borderRadius: 22, padding: 20, marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, color: GOLD, ...font }}>
              <span>{c.managedMine}{activeManaged.ref ? ` · ${activeManaged.ref}` : ''}</span>
              <span>{c.stageOf(idx + 1, MANAGED_STAGES.length)}</span>
            </div>
            <div style={{ fontSize: 21, fontWeight: 800, lineHeight: 1.4, overflowWrap: 'anywhere', ...font }}>
              {activeManaged.title ? <TranslatedText text={activeManaged.title} lang={lang} /> : c.managedK}
            </div>
            <div aria-hidden="true" style={{ display: 'flex', gap: 4 }}>
              {MANAGED_STAGES.map((st, i) => (
                <span key={st.key} style={{ flex: 1, height: 5, borderRadius: 3, background: i < idx ? '#FAF8F5' : i === idx ? GOLD : '#4A443C' }} />
              ))}
            </div>
            <div style={{ fontSize: 15, color: '#E3DBCF', ...font }}>
              {activeManaged.status === 'offer_ready' ? c.offerReady : label(stage)}
              {nextStage && activeManaged.status !== 'offer_ready' ? ` — ${c.next(label(nextStage))}` : ''}
            </div>
            <button type="button" onClick={() => onOpenManaged?.(activeManaged.requestId)}
              style={{ height: 46, borderRadius: 12, border: 'none', background: '#FAF8F5', color: INK, fontSize: 16, fontWeight: 700, cursor: 'pointer', ...font }}>
              {c.track}
            </button>
          </div>
        );
      })() : (
        <div data-managed-invite="1" style={{ background: INK, color: '#FAF8F5', borderRadius: 22, padding: 20, marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 13, color: GOLD, ...font }}>{c.managedK}</div>
          <div style={{ fontSize: 21, fontWeight: 800, lineHeight: 1.4, ...font }}>{c.managedInviteTitle}</div>
          <p style={{ fontSize: 15, color: '#E3DBCF', margin: 0, lineHeight: 1.7, ...font }}>{c.managedInvitePitch}</p>
          <button type="button" onClick={onNewManaged}
            style={{ height: 46, borderRadius: 12, border: 'none', background: '#FAF8F5', color: INK, fontSize: 16, fontWeight: 700, cursor: 'pointer', ...font }}>
            {c.managedStart}
          </button>
        </div>
      )}

      {/* needs your action */}
      {needs.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', ...font }}>{c.needs}</h2>
          <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 18, overflow: 'hidden' }}>
            {needs.map((it, i) => {
              const isRed = RED_ACTIONS.has(it.action);
              const open = () => (it.kind === 'message' ? onOpenMessages?.() : onOpenRequests?.(it));
              return (
                <button key={it.key} type="button" onClick={open} data-needs-item={it.kind}
                  style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 12, textAlign: isAr ? 'right' : 'left', background: 'none', border: 'none', borderBottom: i < needs.length - 1 ? '1px solid var(--border-subtle)' : 'none', padding: '14px 16px', minHeight: 44, cursor: 'pointer' }}>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', overflowWrap: 'anywhere', ...font }}>{it.title ? <TranslatedText text={it.title} lang={lang} /> : (names[it.partyId] || c.party)}</span>
                    <span style={{ fontSize: 14, color: 'var(--text-secondary)', ...font }}>{c.kinds[it.kind]}{waitingLabel(it.waitingSince, lang) ? ` · ${waitingLabel(it.waitingSince, lang)}` : ''}</span>
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: isRed ? RED : 'var(--bronze-deep, #6E5A3F)', whiteSpace: 'nowrap', ...font }}>{c.acts[it.action] || ''}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* start a new request — the three request types */}
      <div style={{ marginTop: 26 }}>
        <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', ...font }}>{c.startNew}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
          {[['managed', onNewManaged], ['rfq', onNewRfq], ['idea', onNewIdea]].map(([k, go]) => (
            <button key={k} type="button" onClick={go} data-new-request={k}
              style={{ display: 'flex', flexDirection: 'column', gap: 4, background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '14px 12px', minHeight: 44, textAlign: isAr ? 'right' : 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', ...font }}>{c.newTiles[k][0]}</span>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', ...font }}>{c.newTiles[k][1]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* activity */}
      <div style={{ marginTop: 26 }}>
        <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', ...font }}>{c.activity}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
          <Stat v={counts.in_progress || 0} k={c.active} go={onOpenRequests} />
          <Stat v={offersNew} k={c.offers} go={onOpenRequests} />
          <Stat v={counts.completed || 0} k={c.done} go={onOpenRequests} />
        </div>
      </div>
    </div>
  );
}
